import React, { useEffect, useState, useMemo } from 'react'
import useApi from 'hooks/useApi'
import {Span} from 'components/datatable'
import {
  DataTable,
  Table,
  Select,
  Button,
  Modal,
  TextArea,
  Checkbox,
  LoadingFull,
} from '@arnsbogroup/react-voicearchive'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useIntl } from 'react-intl'
import messages from './messages'

export default NiceModal.create(({ task, selectedTaskIds = [] }) => {
  const modal = useModal()
  const [loading, setLoading] = useState(false)
  const [checkedTasks, setCheckedTasks] = useState(() => {
    return [
      ... new Set([...selectedTaskIds, task?.uid])
    ].map((id) => +id)
  })
  const [reporter, setReporter] = useState(null);
  const [processArea, setProcessArea] = useState(null);
  const [errorLevel, setErrorLevel] = useState(null);
  const [comment, setComment] = useState('');
  const [createRetake, setCreateRetake] = useState(true);
  const [copySourceFiles, setCopySourceFiles] = useState(false);
  const { formatMessage } = useIntl()
  const { formatDate, formatTime } = useIntl()

  if (!task) {
    return (
      <Modal
        show={modal.visible}
        onClose={() => modal.remove()}
        onOutsideClick={() => modal.remove()}
      >
        Please select the task you want to be primary subject of incident report, and try again.
      </Modal>
    )
  }

  const [state] = useApi(`/dialogdisplay/registerIncident.json?taskId=${task.uid}`)

  const onSubmit = () => {
    setLoading(true)
    const body = new FormData()

    state.tasks
      .map(({ uid }) => uid)
      .filter((taskId) => checkedTasks.includes(+taskId))
      .forEach((taskId) => {
        body.append('taskIds[]', taskId)
      })

    body.append('foundById', reporter ? reporter.value : null)
    body.append('processAreaId', processArea ? processArea.value : null)
    body.append('comment', comment)
    body.append('create_retake', createRetake)
    body.append('copy_source_files', copySourceFiles)
    body.append('errorLevelId', errorLevel?.value)
    fetch(`/rest/v2/incident`, { 
        method: 'POST',
        body
      })
      .then((response) => response.json())
      .then((response) => {
        setLoading(false)

        if (!response.success) {
          toastr.error(response.errors.join(', '))
        } else {
          toastr.success("Incident created")
          modal.resolve()
          modal.remove()
        }
      })
      .catch((error) => toastr.error(error))
  }

  const checkTask = (uid) => {
    if (checkedTasks.includes(+uid)) {
      setCheckedTasks(checkedTasks.filter((taskId) => +taskId !== +uid))
    } else {
      setCheckedTasks([...checkedTasks, +uid])
    }
  }

  const errorLevelOptions = useMemo(() => (
    state?.errorLevels.map((errorLevel) => {
      if (errorLevel.children) {
        return [errorLevel, ...errorLevel.children.map((child) => {
          child.name = "\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0" + child.name
          return child
        })]
      }
      return errorLevel
    }).flat() || []
  ), [state])

  return (
    <Modal
      show={modal.visible}
      onClose={() => modal.remove()}
      onOutsideClick={() => modal.remove()}
      onSubmit={() => loading ? undefined : onSubmit()}
      title={formatMessage(messages.title)}
      dialogPanelClasses="max-w-2xl overflow-x-scroll"
    >
      { !state ? (
        <LoadingFull />
      ) : (
        <div className="space-y-4">
          <span className="font-bold text-sm">{formatMessage(messages.selectTasks)}</span>

          <div className="max-h-96 overflow-scroll">
            <DataTable
              className="table-fixed"
              components={{
                Tr: ({ childProps = {}, ...innerProps }) => {
                  return (
                    <Table.Tr 
                      title={childProps.unavailability ?
                        childProps.unavailability.map((entry) => `${formatDate(entry.dateStart * 1000)} ${formatTime(entry.dateStart * 1000)} - ${formatDate(entry.dateEnd * 1000)} ${formatTime(entry.dateEnd * 1000)}`).join('\n') :
                        ''}
                      className={childProps.unavailability ?
                        (childProps.unavailability.some(item => item.dateEnd > Date.now() / 1000 && item.dateStart < Date.now() / 1000) ?
                          "bg-red-200" :
                          "bg-yellow-200"
                        ) : ''}
                      {...innerProps }
                    />
                  )
                }
              }}
              head={{
                selected: { label: '#', className: 'w-12' },
                name: { sortable: true, label: 'Name', className: 'truncate' }
              }}
              body={{
                selected: (entry) => (
                  <Checkbox
                    disabled={entry.uid === task.uid}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => checkTask(+entry.uid)}
                    checked={checkedTasks.some((taskId) => taskId == entry.uid)}
                  />
                ),
                name: Span
              }}
              entries={state.tasks}
            />
          </div>

          <Select
            label={formatMessage(messages.selectReporter)}
            size="xs"
            value={reporter}
            options={ state.reporters.map((reporter) => ({ value: reporter.id, label: reporter.id })) }
            onChange={(selected) => setReporter(selected)}
          />
          
          <Select
            label={formatMessage(messages.selectProcessArea)}
            size="xs"
            value={processArea}
            options={ state.processAreas.map((processArea) => ({ value: processArea.id, label: processArea.name })) }
            onChange={(selected) => setProcessArea(selected)}
          />
          
          <Select
            label={formatMessage(messages.selectIncidentType)}
            size="xs"
            value={errorLevel}
            options={ errorLevelOptions.map((errorLevel) => ({ value: errorLevel.id, label: errorLevel.name })) }
            onChange={(selected) => setErrorLevel(selected)}
          />

          <TextArea
            label={formatMessage(messages.comment)}
            rows={6}
            value={comment}
            onChange={({ target: { value } }) => setComment(value)}
          />

          <div className="w-full text-right mt-5 flex justify-between items-center">
            <div>
              <Checkbox
                onClick={(e) => e.stopPropagation()}
                onChange={() => {setCreateRetake(!createRetake)}}
                checked={createRetake}
                label={formatMessage(messages.registerRetake)}  
              />
              
              <Checkbox
                onClick={(e) => e.stopPropagation()}
                onChange={() => {setCopySourceFiles(!copySourceFiles)}}
                checked={copySourceFiles}
                label={formatMessage(messages.copySourceFiles)} 
              />
            </div>

            <Button state="negative" onClick={() => onSubmit()} disabled={loading} >
              {loading ? formatMessage(messages.loading) : formatMessage(messages.submit)}
            </Button>
          </div>
        </div>
      ) }
    </Modal>
  )
})
